/**
 * Woven-top silhouettes: woven_shirt, oxford_shirt, flannel_shirt,
 * blouse, tunic.
 */
import { OUTLINE_ATTRS, DETAIL_ATTRS, DETAIL_FILLED_ATTRS, STITCH_ATTRS } from "./_attrs.js";
import { partGroup } from "./_part-attrs.js";
import { pocketPatch, neckLabel, yokeSeam } from "./_primitives.js";

import type { FlatView } from "../flat-svg.js";

/* ── Woven shirt (long sleeve, collar, placket + buttons, cuffs) ───── */
export function woven_shirtFlat(view: FlatView): string {
  const buttons = [168, 216, 264, 312, 360, 404]
    .map((y) => `<circle cx="200" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  ${partGroup("body", `<path ${OUTLINE_ATTRS} d="M 162 84 L 114 100 L 74 230 L 56 336 L 104 350 L 122 268 L 130 180 L 134 428 Q 200 440 266 428 L 270 180 L 278 268 L 296 350 L 344 336 L 326 230 L 286 100 L 238 84 Q 200 70 162 84 Z"/>
  ${view === "front"
    ? `<rect x="188" y="98" width="24" height="11" rx="1.5" ${STITCH_ATTRS}/>
  <rect x="218" y="172" width="34" height="38" rx="2" ${DETAIL_ATTRS}/>
  <path ${STITCH_ATTRS} d="M 222 176 L 248 176 L 248 206 L 222 206 Z"/>`
    : `<path ${DETAIL_ATTRS} d="M 130 140 Q 200 156 270 140"/>
  <path ${STITCH_ATTRS} d="M 132 144 Q 200 160 268 144"/>
  <path ${STITCH_ATTRS} d="M 132 134 L 268 134"/>
  <path ${DETAIL_ATTRS} d="M 130 128 L 270 128"/>
  <path ${STITCH_ATTRS} d="M 196 134 L 196 168 M 204 134 L 204 168"/>`}`)}
  ${partGroup("collar", view === "front"
    ? `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 94 L 160 94 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 160 94 L 200 118 L 240 94 L 254 106 L 206 138 L 200 132 L 194 138 L 146 106 Z"/>`
    : `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 96 L 160 96 Z"/>`)}
  ${view === "front" ? partGroup("placket", `<path ${DETAIL_ATTRS} d="M 194 136 L 192 430"/>
  <path ${DETAIL_ATTRS} d="M 206 136 L 208 430"/>
  <path ${STITCH_ATTRS} d="M 198 140 L 196 428"/>
  <path ${STITCH_ATTRS} d="M 202 140 L 204 428"/>
  ${buttons}`) : ""}
  ${partGroup("sleeve_left", `<path ${STITCH_ATTRS} d="M 114 102 Q 126 140 130 180"/>`)}
  ${partGroup("sleeve_right", `<path ${STITCH_ATTRS} d="M 286 102 Q 274 140 270 180"/>`)}
  ${partGroup("cuff_left", `<path ${DETAIL_FILLED_ATTRS} d="M 56 336 L 104 350 L 98 376 L 50 362 Z"/>
  <path ${STITCH_ATTRS} d="M 55 342 L 101 356"/>`)}
  ${partGroup("cuff_right", `<path ${DETAIL_FILLED_ATTRS} d="M 344 336 L 296 350 L 302 376 L 350 362 Z"/>
  <path ${STITCH_ATTRS} d="M 345 342 L 299 356"/>`)}
  ${partGroup("hem", `<path ${STITCH_ATTRS} d="M 135 420 Q 200 431 265 420"/>`)}
</g>`;
}

/* ── Oxford shirt (button-down collar points, chest pocket) ──────── */
export function oxford_shirtFlat(view: FlatView): string {
  const buttons = [168, 218, 268, 318, 368, 410]
    .map((y) => `<circle cx="200" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 162 84 L 114 100 L 74 230 L 56 336 L 104 350 L 122 268 L 130 180 L 134 428 Q 200 440 266 428 L 270 180 L 278 268 L 296 350 L 344 336 L 326 230 L 286 100 L 238 84 Q 200 70 162 84 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 56 336 L 104 350 L 98 376 L 50 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 344 336 L 296 350 L 302 376 L 350 362 Z"/>
  <path ${STITCH_ATTRS} d="M 55 342 L 101 356"/>
  <path ${STITCH_ATTRS} d="M 345 342 L 299 356"/>
  <path ${STITCH_ATTRS} d="M 114 102 Q 126 140 130 180"/>
  <path ${STITCH_ATTRS} d="M 286 102 Q 274 140 270 180"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 94 L 160 94 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 160 94 L 200 118 L 240 94 L 254 106 L 206 138 L 200 132 L 194 138 L 146 106 Z"/>
  <circle cx="170" cy="108" r="3" ${DETAIL_FILLED_ATTRS}/>
  <circle cx="230" cy="108" r="3" ${DETAIL_FILLED_ATTRS}/>
  <path ${DETAIL_ATTRS} d="M 194 136 L 192 430"/>
  <path ${DETAIL_ATTRS} d="M 206 136 L 208 430"/>
  <path ${STITCH_ATTRS} d="M 198 140 L 196 428"/>
  <path ${STITCH_ATTRS} d="M 202 140 L 204 428"/>
  ${buttons}
  ${pocketPatch(148, 172, 34, 38)}
  <rect x="188" y="98" width="24" height="11" rx="1.5" ${STITCH_ATTRS}/>`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 96 L 160 96 Z"/>
  ${yokeSeam(128, 130, 270)}
  <path ${STITCH_ATTRS} d="M 196 134 L 196 168 M 204 134 L 204 168"/>`
  }
  <path ${STITCH_ATTRS} d="M 135 420 Q 200 431 265 420"/>
</g>`;
}

/* ── Flannel shirt (same structure, chest pockets both sides) ─────── */
export function flannel_shirtFlat(view: FlatView): string {
  const buttons = [168, 218, 268, 318, 368, 410]
    .map((y) => `<circle cx="200" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 162 84 L 114 100 L 74 230 L 56 336 L 104 350 L 122 268 L 130 180 L 134 428 Q 200 440 266 428 L 270 180 L 278 268 L 296 350 L 344 336 L 326 230 L 286 100 L 238 84 Q 200 70 162 84 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 56 336 L 104 350 L 98 376 L 50 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 344 336 L 296 350 L 302 376 L 350 362 Z"/>
  <path ${STITCH_ATTRS} d="M 55 342 L 101 356"/>
  <path ${STITCH_ATTRS} d="M 345 342 L 299 356"/>
  <path ${STITCH_ATTRS} d="M 114 102 Q 126 140 130 180"/>
  <path ${STITCH_ATTRS} d="M 286 102 Q 274 140 270 180"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 94 L 160 94 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 160 94 L 200 118 L 240 94 L 254 106 L 206 138 L 200 132 L 194 138 L 146 106 Z"/>
  <path ${DETAIL_ATTRS} d="M 194 136 L 192 430"/>
  <path ${DETAIL_ATTRS} d="M 206 136 L 208 430"/>
  <path ${STITCH_ATTRS} d="M 198 140 L 196 428"/>
  <path ${STITCH_ATTRS} d="M 202 140 L 204 428"/>
  ${buttons}
  ${pocketPatch(148, 172, 34, 38)}
  ${pocketPatch(218, 172, 34, 38)}
  <rect x="188" y="98" width="24" height="11" rx="1.5" ${STITCH_ATTRS}/>`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 96 L 160 96 Z"/>
  ${yokeSeam(128, 130, 270)}
  <path ${STITCH_ATTRS} d="M 196 128 L 196 162 M 204 128 L 204 162"/>`
  }
  <path ${STITCH_ATTRS} d="M 135 420 Q 200 431 265 420"/>
</g>`;
}

/* ── Blouse (feminine cut, gather at neckline, puff cap sleeves) ──── */
export function blouseFlat(view: FlatView): string {
  const neckDip = view === "front" ? 122 : 96;
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 160 82 L 112 98 L 70 226 L 52 322 L 100 336 L 118 258 L 128 176 L 134 432 Q 200 444 266 432 L 272 176 L 282 258 L 300 336 L 348 322 L 330 226 L 288 98 L 240 82 Q 200 ${neckDip - 24} 160 82 Z"/>
  <path ${DETAIL_ATTRS} d="M 160 82 Q 200 ${neckDip} 240 82"/>
  <path ${STITCH_ATTRS} d="M 163 87 Q 200 ${neckDip + 6} 237 87"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 52 322 L 100 336 L 94 358 L 46 344 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 348 322 L 300 336 L 306 358 L 354 344 Z"/>
  <path ${STITCH_ATTRS} d="M 112 100 Q 124 138 128 176"/>
  <path ${STITCH_ATTRS} d="M 288 100 Q 276 138 272 176"/>
  ${
    view === "front"
      ? `<path ${DETAIL_ATTRS} d="M 170 92 Q 178 102 180 112"/>
  <path ${DETAIL_ATTRS} d="M 230 92 Q 222 102 220 112"/>
  <path ${DETAIL_ATTRS} d="M 175 96 Q 183 108 185 118"/>
  <path ${DETAIL_ATTRS} d="M 225 96 Q 217 108 215 118"/>
  <path ${STITCH_ATTRS} d="M 135 422 Q 200 434 265 422"/>`
      : `<path ${STITCH_ATTRS} d="M 136 130 L 264 130"/>
  ${neckLabel(200, 82)}
  <path ${STITCH_ATTRS} d="M 135 422 Q 200 434 265 422"/>`
  }
</g>`;
}

/* ── Tunic (longer body, hip-length, side slits) ──────────────────── */
export function tunicFlat(view: FlatView): string {
  const buttons = [170, 220, 270, 320, 370, 416]
    .map((y) => `<circle cx="200" cy="${y}" r="4.5" ${DETAIL_FILLED_ATTRS}/>`)
    .join("\n  ");
  return `<g>
  <path ${OUTLINE_ATTRS} d="M 162 84 L 114 100 L 74 230 L 56 336 L 104 350 L 122 268 L 130 180 L 124 450 Q 200 462 276 450 L 270 180 L 278 268 L 296 350 L 344 336 L 326 230 L 286 100 L 238 84 Q 200 70 162 84 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 56 336 L 104 350 L 98 376 L 50 362 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 344 336 L 296 350 L 302 376 L 350 362 Z"/>
  <path ${STITCH_ATTRS} d="M 55 342 L 101 356"/>
  <path ${STITCH_ATTRS} d="M 345 342 L 299 356"/>
  <path ${STITCH_ATTRS} d="M 114 102 Q 126 140 130 180"/>
  <path ${STITCH_ATTRS} d="M 286 102 Q 274 140 270 180"/>
  <path ${DETAIL_ATTRS} d="M 124 416 L 124 450"/>
  <path ${DETAIL_ATTRS} d="M 276 416 L 276 450"/>
  ${
    view === "front"
      ? `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 94 L 160 94 Z"/>
  <path ${DETAIL_FILLED_ATTRS} d="M 160 94 L 200 118 L 240 94 L 254 106 L 206 138 L 200 132 L 194 138 L 146 106 Z"/>
  <path ${DETAIL_ATTRS} d="M 194 136 L 192 448"/>
  <path ${DETAIL_ATTRS} d="M 206 136 L 208 448"/>
  <path ${STITCH_ATTRS} d="M 198 140 L 196 446"/>
  <path ${STITCH_ATTRS} d="M 202 140 L 204 446"/>
  ${buttons}
  <rect x="188" y="98" width="24" height="11" rx="1.5" ${STITCH_ATTRS}/>`
      : `<path ${DETAIL_FILLED_ATTRS} d="M 166 82 C 178 74 222 74 234 82 L 240 96 L 160 96 Z"/>
  ${yokeSeam(128, 130, 270)}
  <path ${STITCH_ATTRS} d="M 196 128 L 196 162 M 204 128 L 204 162"/>`
  }
  <path ${STITCH_ATTRS} d="M 126 440 Q 200 452 274 440"/>
</g>`;
}
